import { useState, useEffect } from 'react';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useCertificationTypes } from '@/hooks/useCertificationTypes';

interface CertificationSelectorProps {
  selectedTypeId: string | null;
  onTypeSelect: (typeId: string | null) => void;
  disabled?: boolean;
}

export function CertificationSelector({
  selectedTypeId,
  onTypeSelect,
  disabled = false,
}: CertificationSelectorProps) {
  const { categories, types, getTypesByCategory, isLoading } = useCertificationTypes();
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null);

  // Pre-select category if type is already selected
  useEffect(() => {
    if (selectedTypeId && types.length > 0) {
      const selectedType = types.find(t => t.id === selectedTypeId);
      if (selectedType) {
        setSelectedCategoryId(selectedType.category_id);
      }
    }
  }, [selectedTypeId, types]);

  const handleCategoryChange = (categoryId: string) => {
    setSelectedCategoryId(categoryId);
    onTypeSelect(null); // Reset type selection when category changes
  };

  const handleTypeChange = (typeId: string) => {
    onTypeSelect(typeId);
  };

  const categoryTypes = selectedCategoryId
    ? getTypesByCategory(selectedCategoryId)
    : [];

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label>Certification Category</Label>
        <Select
          value={selectedCategoryId || ''}
          onValueChange={handleCategoryChange}
          disabled={disabled || isLoading}
        >
          <SelectTrigger>
            <SelectValue placeholder="Select category..." />
          </SelectTrigger>
          <SelectContent>
            {categories.map((category) => (
              <SelectItem key={category.id} value={category.id}>
                {category.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {selectedCategoryId && (
        <div className="space-y-2">
          <Label>Certification Type</Label>
          <Select
            value={selectedTypeId || ''}
            onValueChange={handleTypeChange}
            disabled={disabled || isLoading}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select certification..." />
            </SelectTrigger>
            <SelectContent>
              {categoryTypes.map((type) => (
                <SelectItem key={type.id} value={type.id}>
                  {type.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}
    </div>
  );
}
